// sos-call-service.js — Orquestador imperativo del feature SOS/Colibrí en map-v2
// Encapsula VideoSDK, socket emits/listeners, audio loop, nominatim y teardown.
// Ver spec: docs/superpowers/specs/2026-04-12-sos-colibri-design.md

import ApiService from '@/util/api.service'

const AUDIO_SRC = '/sounds/sos-alert.wav'

class SOSCallService {
  constructor({ store, socket }) {
    if (!store) throw new Error('[sos-call-service] store is required')
    this.store = store
    this.socket = socket || null // puede ser null si permission_sinaloaSOS es false
    // o si todavía no está disponible (se setea con setSocket())

    this.audio = typeof Audio !== 'undefined' ? new Audio(AUDIO_SRC) : null
    if (this.audio) {
      this.audio.loop = true
      this.audio.preload = 'auto'
    }

    this._destroyed = false
    this._listenersRegistered = false
    if (this.socket) this._registerSocketListeners()
  }

  // Permite setear o reemplazar el socket después del constructor
  // (útil porque en MapV2.vue el service se instancia en created() pero el
  // socket aparece después en mounted() al crear SocketIOHandler).
  setSocket(socket) {
    this.socket = socket || null
    if (this.socket && !this._listenersRegistered) {
      this._registerSocketListeners()
    }
  }

  destroy() {
    this._destroyed = true
    // Remover el listener send_message: el socket /ws/appclient es
    // compartido y long-lived, así que dejarlo atado provoca duplicados
    // de mensajes cada vez que MapV2 se destruye y recrea (navegación).
    if (this.socket && this._listenersRegistered) {
      try { this.socket.off('send_message') } catch (e) { /* silent */ }
      this._listenersRegistered = false
    }
    this._stopAudio()
    this.leaveMeeting()
  }

  // —— Socket listeners (se registran cuando hay socket) ——
  _registerSocketListeners() {
    if (!this.socket || this._listenersRegistered) return
    // El chat entrante se procesa aquí para mantener la lógica cohesionada.
    // send_message del reportante (o del eco del operador) llega por este canal.
    this.socket.on('send_message', (msg) => {
      if (this._destroyed) return
      this.store.commit('sosMessages/ADD_MESSAGE', msg)
    })
    this._listenersRegistered = true
  }

  // —— Audio ——
  _startAudio() {
    if (!this.audio) return
    this.audio.play().catch(() => { /* autoplay policy — silent */ })
  }

  _stopAudio() {
    if (!this.audio) return
    try {
      this.audio.pause()
      this.audio.currentTime = 0
    } catch (e) { /* silent */ }
  }

  // —— Banner queue ——
  showBannerFor(callData) {
    if (!callData || !callData._id) return
    this.store.commit('sos-call-v2/PUSH_BANNER_QUEUE', callData)
    const queue = this.store.state['sos-call-v2'].bannerQueue
    // Respeta el toggle del toolbar (config > Alertas en pantalla >
    // Llamadas SOS). Si el operador desactivo este tipo, la llamada sigue
    // entrando al store para que aparezca en el sidebar y se pueda
    // atender, pero no hay banner flotante ni ring loop.
    const bannerTypes = this.store.state['map-v2'] && this.store.state['map-v2'].bannerAlertTypes
    const sosBannerEnabled = !bannerTypes || bannerTypes.sos_call !== false
    if (queue.length > 0 && sosBannerEnabled) this._startAudio()
  }

  descartarBanner(callId) {
    this.store.commit('sos-call-v2/POP_BANNER_QUEUE', callId)
    const queue = this.store.state['sos-call-v2'].bannerQueue
    if (queue.length === 0) this._stopAudio()
  }

  // —— VideoSDK lifecycle ——
  async joinMeeting(callData) {
    if (!callData) return
    if (typeof window === 'undefined' || !window.VideoSDK) {
      if (window.VMA && window.VMA.showError) {
        window.VMA.showError({ title: 'VideoSDK no disponible' })
      }
      console.warn('[sos-call-service] window.VideoSDK is undefined')
      return
    }
    if (!callData.videoSdkToken || !callData.roomData || !callData.roomData.roomId) {
      console.warn('[sos-call-service] llamada sin videoSdkToken o roomData')
      return
    }

    try {
      window.VideoSDK.config(callData.videoSdkToken)
      const user = this.store.getters['auth/getUser'] || {}
      const meeting = window.VideoSDK.initMeeting({
        meetingId: callData.roomData.roomId,
        name: user.name || 'Operador',
        // Mic apagado por defecto: el operador debe activarlo manualmente
        // con el botón del header antes de hablar con el reportante.
        micEnabled: false,
        webcamEnabled: false,
      })

      meeting.on('meeting-joined', () => {
        this.store.commit('sos-call-v2/SET_MIC_ON', false)
        this._attachParticipants(meeting)
      })
      meeting.on('participant-joined', (p) => this._attachStreamsOf(p))
      meeting.on('error', (err) => {
        console.error('[sos-call-service] meeting error', err)
        if (window.VMA && window.VMA.showError) {
          window.VMA.showError({ title: 'Error en videollamada' })
        }
      })

      meeting.join()
      this.store.commit('sos-call-v2/SET_MEETING', meeting)
    } catch (err) {
      console.error('[sos-call-service] joinMeeting failed', err)
      if (window.VMA && window.VMA.showError) {
        window.VMA.showError({ title: 'No se pudo unir a la videollamada' })
      }
    }
  }

  _attachParticipants(meeting) {
    if (!meeting || !meeting.participants) return
    // Adjuntar streams a los participantes que ya estaban cuando nos unimos
    meeting.participants.forEach((p) => this._attachStreamsOf(p))
  }

  _attachStreamsOf(participant) {
    if (!participant || !participant.on) return

    // Listener para streams futuros
    participant.on('stream-enabled', (stream) => {
      this._processStream(stream)
    })

    participant.on('stream-disabled', (stream) => {
      if (!stream) return
      if (stream.kind === 'audio') {
        this.store.commit('sos-call-v2/SET_AUDIO_STREAM', null)
      } else if (stream.kind === 'video') {
        this.store.commit('sos-call-v2/SET_VIDEO_STREAM', null)
      }
    })

    // Capturar streams que ya existían antes de que el operador se uniera
    // (paridad con chatSos.vue:250-259)
    if (participant.streams) {
      var streamList = participant.streams instanceof Map
        ? Array.from(participant.streams.values())
        : Object.values(participant.streams || {})
      streamList.forEach((stream) => this._processStream(stream))
    }
  }

  _processStream(stream) {
    if (!stream || !stream.track) return
    try {
      const ms = new MediaStream()
      ms.addTrack(stream.track)
      if (stream.kind === 'audio') {
        this.store.commit('sos-call-v2/SET_AUDIO_STREAM', ms)
      } else if (stream.kind === 'video') {
        this.store.commit('sos-call-v2/SET_VIDEO_STREAM', ms)
      }
    } catch (e) {
      console.error('[sos-call-service] stream processing failed', e)
    }
  }

  async leaveMeeting() {
    const meeting = this.store.state['sos-call-v2'].currentMeeting
    if (meeting) {
      try { meeting.leave() } catch (e) { console.error('[sos-call-service] leave failed', e) }
    }
    // Detener tracks de ambos streams
    const v = this.store.state['sos-call-v2'].videoStream
    const a = this.store.state['sos-call-v2'].audioStream
    try { if (v) v.getTracks().forEach((t) => t.stop()) } catch (e) { /* silent */ }
    try { if (a) a.getTracks().forEach((t) => t.stop()) } catch (e) { /* silent */ }
    this.store.commit('sos-call-v2/RESET_CALL_STATE')
  }

  // —— Controles durante la llamada ——
  toggleMic() {
    const meeting = this.store.state['sos-call-v2'].currentMeeting
    if (!meeting) return
    const isOn = this.store.state['sos-call-v2'].isMicOn
    try {
      if (isOn) meeting.muteMic()
      else meeting.unmuteMic()
      this.store.commit('sos-call-v2/SET_MIC_ON', !isOn)
    } catch (e) {
      console.error('[sos-call-service] toggleMic failed', e)
      if (window.VMA && window.VMA.showError) {
        window.VMA.showError({ title: 'Permiso de micrófono denegado' })
      }
    }
  }

  toggleCam() {
    const meeting = this.store.state['sos-call-v2'].currentMeeting
    if (!meeting) return
    const isOn = this.store.state['sos-call-v2'].isCamOn
    try {
      if (isOn) meeting.disableWebcam()
      else meeting.enableWebcam()
      this.store.commit('sos-call-v2/SET_CAM_ON', !isOn)
    } catch (e) {
      console.error('[sos-call-service] toggleCam failed', e)
      if (window.VMA && window.VMA.showError) {
        window.VMA.showError({ title: 'Permiso de cámara denegado' })
      }
    }
  }

  swapRemoteCamera() {
    if (!this.socket) return
    // Paridad exacta con chatSos.vue:152-159
    this.socket.emit('set_camera', 'changeCamera')
  }

  sendMessage(text) {
    if (!text || !text.trim() || !this.socket) return
    var callId = this.store.state.map && this.store.state.map.sosActiveCall
      ? this.store.state.map.sosActiveCall._id : null

    this.socket.emit('send_message', text.trim())

    // Optimistic local add — message appears immediately in the UI.
    // If the server echoes it back, the dedup in ADD_MESSAGE will replace this.
    if (callId) {
      const user = this.store.getters['auth/getUser'] || {}
      this.store.commit('sosMessages/ADD_MESSAGE', {
        emerCallId: callId,
        message: text.trim(),
        sender: 'operator',
        // Nombre del operador para distinguir quién escribe en una llamada con
        // varios operadores. El echo del server trae su propio senderName y
        // reemplaza este optimista vía el dedup de ADD_MESSAGE.
        senderName: user.name || user.username || 'Operador',
        createdAt: new Date().toISOString(),
        _optimistic: true,
      })
    }
  }

  // —— Atender / cerrar ——
  async atender(callData) {
    if (!callData || !callData._id) return

    // Notificar a MapV2 para que centre el mapa en la ubicación del
    // reportante antes de abrir el overlay. Se llama desde ambos entry
    // points (SOSBanner y drawer) porque todos pasan por este método.
    try { this._onBeforeAtender(callData) } catch (e) { /* silent */ }

    // Verificar que la llamada sigue activa (race con end_call)
    const active = this.store.state.alerts && this.store.state.alerts.sosCallAlerts
    if (active && Array.isArray(active)) {
      const stillAlive = active.some((c) => (c.id || c._id) === callData._id)
      if (!stillAlive) {
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({ title: 'La llamada ya finalizó', color: 'warning' })
        }
        this.store.commit('sos-call-v2/POP_BANNER_QUEUE', callData._id)
        return
      }
    }

    // Si hay otra llamada activa, pedir confirmación
    const currentActive = this.store.state.map && this.store.state.map.sosActiveCall
    if (currentActive && currentActive._id && currentActive._id !== callData._id) {
      try {
        await window.VMA.$confirm({
          title: 'Llamada en curso',
          description: 'Ya estás atendiendo una llamada. ¿Finalizar la actual y atender esta?',
          acceptText: 'Sí, atender esta',
          cancelText: 'Cancelar',
        })
      } catch (e) {
        return
      }
      await this.finalizarLlamada()
    }

    this._stopAudio()
    if (this.socket) this.socket.emit('take_call', callData._id)
    this.store.commit('sos-call-v2/POP_BANNER_QUEUE', callData._id)

    // Optimistic: marcar la llamada como IN_PROGRESS sin esperar el echo del
    // server, para que el sidebar muestre "En atención" de inmediato.
    // Clonamos para no mutar el objeto compartido con bannerQueue/store.
    const updatedCall = { ...callData, status: 'IN_PROGRESS' }
    this.store.dispatch('alerts/addSOSCallAlerts', updatedCall)

    this.store.dispatch('map/setActiveCall', updatedCall)
    this.store.commit('sos-call-v2/SET_OVERLAY_OPEN', true)
    this.store.commit('sos-call-v2/SET_LAST_ACTIVE_CALL_ID', updatedCall._id)
    this.store.commit('map-v2/SET_PANEL', { panel: 'sosLeft', value: true })
    // Cerrar sidebar de alertas al abrir SOS para evitar congestionar el
    // leftstack (rail 44 + sidebar 280 + SOS 280 = 604 px). Murphy se
    // respeta tal cual esté.
    this.store.commit('map-v2/SET_PANEL', { panel: 'sidebar', value: false })
    this._onCallTaken(updatedCall._id)

    // Esperar a que SOSPanel monte sus $refs (video/audio elements)
    // antes de unirse al meeting y recibir streams.
    await new Promise((r) => setTimeout(r, 150))

    await this.joinMeeting(callData)
    this.fetchNominatim(callData) // fire & forget
  }

  cerrarOverlay() {
    // Solo oculta el panel, NO cuelga la llamada
    this.store.commit('sos-call-v2/SET_OVERLAY_OPEN', false)
    this.store.commit('map-v2/SET_PANEL', { panel: 'sosLeft', value: false })
  }

  async finalizarLlamada() {
    const activeCall = this.store.state.map && this.store.state.map.sosActiveCall
    const callId = activeCall && activeCall._id

    if (callId && this.socket) {
      try { this.socket.emit('end_call', callId) } catch (e) { /* silent */ }
    }

    // meeting.end() termina para TODOS los participantes (incluido el teléfono).
    // Paridad con chatSos.vue que usa meeting.end(), no meeting.leave().
    const meeting = this.store.state['sos-call-v2'].currentMeeting
    if (meeting) {
      try { meeting.end() } catch (e) { /* silent */ }
    }

    await this.leaveMeeting()

    // Limpieza local — no depender del echo del server.
    // El echo puede llegar con formato diferente o no llegar.
    if (callId) {
      this.store.dispatch('alerts/removeSOSCallAlerts', callId)
      this.store.commit('sosMessages/CLEAR_MESSAGES')
    }
    this.store.commit('sos-call-v2/SET_LAST_ACTIVE_CALL_ID', null)
    this.store.commit('sos-call-v2/SET_OVERLAY_OPEN', false)
    this.store.commit('map-v2/SET_PANEL', { panel: 'sosLeft', value: false })
    this.store.dispatch('map/setActiveCall', null)

    // Notificar a MapV2 para que limpie el marker y la alerta del sidebar.
    // Usamos un evento custom en el store que MapV2 escucha.
    if (callId) {
      this._onCallFinalized(callId)
    }
  }

  // El operador SALE de la llamada sin terminarla: emite leave_call y limpia su
  // UI local (meeting.leave SOLO local, sin meeting.end), pero NO remueve el
  // marker ni la alerta del sidebar — la llamada sigue activa para los demás
  // operadores / el reportante y puede reanudarse (atender de nuevo). Paridad
  // con el botón "DEJAR" / leave_call del legacy chatSos.vue. El echo de
  // leave_call del server llega como onSOSEnd(null), que es no-op, por eso la
  // limpieza local es responsabilidad de este método.
  async dejarLlamada() {
    const activeCall = this.store.state.map && this.store.state.map.sosActiveCall
    const callId = activeCall && activeCall._id

    if (callId && this.socket) {
      try { this.socket.emit('leave_call', callId) } catch (e) { /* silent */ }
    }

    // Salir del meeting SOLO localmente (NO meeting.end, que terminaría para
    // todos). leaveMeeting hace meeting.leave() + detiene tracks + RESET.
    await this.leaveMeeting()

    // Limpieza de la UI local del operador, SIN tocar la llamada en el server
    // (no removeSOSCallAlerts ni _onCallFinalized: debe seguir visible/reanudable).
    this.store.commit('sosMessages/CLEAR_MESSAGES')
    this.store.commit('sos-call-v2/SET_LAST_ACTIVE_CALL_ID', null)
    this.store.commit('sos-call-v2/SET_OVERLAY_OPEN', false)
    this.store.commit('map-v2/SET_PANEL', { panel: 'sosLeft', value: false })
    this.store.dispatch('map/setActiveCall', null)
  }

  // Descarta una llamada que el operador NO está atendiendo (típicamente en
  // CREATED/WAITING_OPERATOR atascada): emite end_call para terminarla
  // server-side y limpia su marker + alerta del sidebar vía el hook
  // _onCallFinalized. NO toca el overlay ni sosActiveCall — si hay una llamada
  // activa distinta, sigue intacta (por eso no llamamos CLEAR_MESSAGES, que
  // borraría el chat de la activa).
  discardCall(callId) {
    if (!callId) return
    if (this.socket) {
      try { this.socket.emit('end_call', callId) } catch (e) { /* silent */ }
    }
    // Limpieza local optimista — no depender del echo del server.
    this.store.dispatch('alerts/removeSOSCallAlerts', callId)
    this._onCallFinalized(callId)
  }

  // Finaliza una llamada por su id (end_call) y limpia su UI de forma optimista:
  // quita la alerta del sidebar y, vía _onCallFinalized, el marker del mapa y el
  // drawer si mostraba esa llamada. NO toca el overlay/panel izquierdo del
  // operador (esa llamada no es la que él atiende; para la propia se usa
  // finalizarLlamada). El server además marca FINISHED y reemite end_call a TODOS
  // los operadores, que limpian su UI en MapV2.onSOSEnd. Usado para finalizar
  // desde el drawer una llamada (p.ej. CLIENT_END_CALL en cola) no atendida.
  finalizeCallById(callId) {
    if (!callId) return
    if (this.socket) {
      try { this.socket.emit('end_call', callId) } catch (e) { /* silent */ }
    }
    this.store.dispatch('alerts/removeSOSCallAlerts', callId)
    this._onCallFinalized(callId)
  }

  // Limpieza LOCAL de la llamada activa del operador, SIN emitir end_call ni
  // tocar el server. Se invoca cuando llega el end_call del server (la llamada
  // terminó para TODOS los participantes): salimos del meeting localmente,
  // limpiamos el chat y cerramos el overlay / panel izquierdo. El marker, la
  // alerta del sidebar y el drawer los limpia MapV2.onSOSEnd, por eso aquí NO
  // llamamos _onCallFinalized.
  async cleanupActiveCallLocal() {
    await this.leaveMeeting()
    this.store.commit('sosMessages/CLEAR_MESSAGES')
    this.store.commit('sos-call-v2/SET_LAST_ACTIVE_CALL_ID', null)
    this.store.commit('sos-call-v2/SET_OVERLAY_OPEN', false)
    this.store.commit('map-v2/SET_PANEL', { panel: 'sosLeft', value: false })
    this.store.dispatch('map/setActiveCall', null)
  }

  // Hook que MapV2 setea para limpiar markers y alertas del sidebar
  _onCallFinalized() {
    // Default no-op — MapV2 lo sobreescribe con setOnCallFinalized()
  }

  setOnCallFinalized(fn) {
    this._onCallFinalized = fn || (() => {})
  }

  // Hook que MapV2 setea para actualizar la alerta del sidebar cuando el
  // operador toma la llamada (no esperamos el echo del server).
  _onCallTaken() {
    // Default no-op — MapV2 lo sobreescribe con setOnCallTaken()
  }

  setOnCallTaken(fn) {
    this._onCallTaken = fn || (() => {})
  }

  // Hook que MapV2 setea para centrar el mapa sobre la ubicación del
  // reportante en el momento en que el operador presiona "Atender"
  // (desde el banner o desde el drawer). Se dispara antes del join al
  // meeting para que el operador vea de inmediato dónde está la llamada.
  _onBeforeAtender() {
    // Default no-op — MapV2 lo sobreescribe con setOnBeforeAtender()
  }

  setOnBeforeAtender(fn) {
    this._onBeforeAtender = fn || (() => {})
  }

  // —— Auto-retomar tras reload ——
  async autoResumeIfNeeded(activeCalls) {
    const lastId = this.store.state['sos-call-v2'].lastActiveCallId
    if (!lastId) return
    if (!Array.isArray(activeCalls)) return

    const call = activeCalls.find((c) => (c._id || c.id) === lastId)
    if (!call) {
      this.store.commit('sos-call-v2/SET_LAST_ACTIVE_CALL_ID', null)
      this.store.commit('sos-call-v2/SET_OVERLAY_OPEN', false)
      return
    }

    // Atender sin el prompt de confirmación ni el stop de audio.
    // Disparamos el hook onBeforeAtender para centrar el mapa sobre la
    // llamada restaurada (consistente con atender() desde banner/drawer).
    try { this._onBeforeAtender(call) } catch (e) { /* silent */ }
    this.store.dispatch('map/setActiveCall', call)
    this.store.commit('sos-call-v2/SET_OVERLAY_OPEN', true)
    this.store.commit('map-v2/SET_PANEL', { panel: 'sosLeft', value: true })
    this.store.commit('map-v2/SET_PANEL', { panel: 'sidebar', value: false })
    await this.joinMeeting(call)
    this.fetchNominatim(call)
  }

  // —— Nominatim reverse geocoding ——
  async fetchNominatim(callData) {
    if (!callData) return
    const pos = callData.lastPosition
    const lat = pos && (pos.latitude || pos.lat)
    const lng = pos && (pos.longitude || pos.lon || pos.lng)
    if (typeof lat !== 'number' || typeof lng !== 'number') return

    try {
      const res = await ApiService({
        url: `/nominatim/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1`,
        method: 'get',
        timeout: 5000,
      })
      const address = (res && res.display_name) || ''
      this.store.commit('sos-call-v2/CACHE_NOMINATIM', {
        callId: callData._id,
        address,
      })
    } catch (e) {
      // silent: el overlay usa coordenadas como fallback
    }
  }
}

export default SOSCallService
